import axios, {
  AxiosInstance,
  InternalAxiosRequestConfig,
  AxiosResponse,
  AxiosRequestConfig,
} from 'axios';
import { ApiError } from '../types';
import { config } from '../config';

type ApiRequestData = any;
type ExtendedAxiosRequestConfig = AxiosRequestConfig & {
  suppressErrorLog?: boolean;
  suppressAuthRedirect?: boolean;
};

// Create axios instance
const api: AxiosInstance = axios.create({
  baseURL: config.apiUrl,
  timeout: 5000, // Kratší timeout
  headers: {
    'Content-Type': 'application/json',
  },
  withCredentials: true, // Enable cookies
});

// Response interceptor
api.interceptors.request.use(
  (config: InternalAxiosRequestConfig) => {
    return config;
  },
  (error) => {
    return Promise.reject(error);
  },
);

// Response interceptor
api.interceptors.response.use(
  (response: AxiosResponse) => {
    return response;
  },
  (error) => {
    // Handle connection errors gracefully
    if (
      error.code === 'ECONNREFUSED' ||
      error.code === 'ECONNRESET' ||
      error.code === 'ETIMEDOUT'
    ) {
      /* eslint-disable-next-line no-console */
      console.debug('API server není dostupný:', error.message);
      // Vrátit fallback response místo nekonečného cyklování
      // Vrátit rejected promise aby to React Query chytilo jako error
      // Return a real Error object to avoid console crashes in extensions
      const connErr = new Error('API server není dostupný. Zkontrolujte připojení.') as any;
      connErr.code = error.code || 'CONNECTION_ERROR';
      connErr.details = error.message;
      connErr.isConnectionError = true;
      return Promise.reject(connErr);
    }

    // Handle common errors
    const requestConfig = (error.config || {}) as ExtendedAxiosRequestConfig;

    if (error.response?.status === 401 && !requestConfig.suppressAuthRedirect) {
      // Token expired or invalid
      // Server has cleared cookie via Set-Cookie header
      // Redirect handled by AuthProvider or manual check
      // NESMIME presmerovat z OAuth callback stranek - ty token teprve ziskavaji
      const isAuthCallback =
        window.location.pathname.includes('/auth/') &&
        window.location.pathname.includes('/callback');
      const isLoginPage = window.location.pathname === '/login';
      if (!isLoginPage && !isAuthCallback) {
        window.location.href = '/login';
      }
    }

    // Log full error for debugging
    if (!requestConfig.suppressErrorLog) {
      console.warn('[API Error] Status:', error.response?.status);
      console.warn('[API Error] Response:', error.response?.data);
      console.warn('[API Error] Config:', error.config?.url, error.config?.method);
    }

    // Transform error to ApiError format
    // Check for 'error' field first (backend returns this), then 'message'
    const apiError = new Error(
      error.response?.data?.error ||
      error.response?.data?.message ||
      error.message ||
      'Chyba připojení k serveru'
    ) as ApiError & Error;
    
    apiError.code = error.response?.status?.toString() || 'CONNECTION_ERROR';
    apiError.details = error.response?.data?.details || null;
    apiError.timestamp = new Date();

    return Promise.reject(apiError);
  },
);

// Generic API functions
// Pozn: API vraci primo data (T), ne ApiResponse<T> wrapper
export const apiClient = {
  get: async <T>(url: string, config?: ExtendedAxiosRequestConfig): Promise<T> => {
    const response = await api.get<T>(url, config);
    return response.data;
  },

  post: async <T>(url: string, data?: ApiRequestData, config?: ExtendedAxiosRequestConfig): Promise<T> => {
    const response = await api.post<T>(url, data, config);
    return response.data;
  },

  put: async <T>(url: string, data?: ApiRequestData, config?: ExtendedAxiosRequestConfig): Promise<T> => {
    const response = await api.put<T>(url, data, config);
    return response.data;
  },

  patch: async <T>(url: string, data?: ApiRequestData, config?: ExtendedAxiosRequestConfig): Promise<T> => {
    const response = await api.patch<T>(url, data, config);
    return response.data;
  },

  delete: async <T>(url: string, config?: ExtendedAxiosRequestConfig): Promise<T> => {
    const response = await api.delete<T>(url, config);
    return response.data;
  },
};

export default api;
